
import {Component, Vue, Prop, Watch} from "vue-property-decorator";
import {Getter} from "vuex-class";
import moment from "moment";
import {Datetime} from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";
import {formRules} from "@/utils/formRules";
import CustomChipLink from "@/components/CustomChipLink.vue";

@Component({
    components: {
        Datetime,
        CustomChipLink,
    },
})
export default class BookingCreate extends Vue {

    get recurrenceList(): any {
        const reccurence = [];
        if (this.startDate !== "" && this.endDate !== "") {
            const start = moment(this.startDate);
            const end = moment(this.endDate);
            const ms = start.diff(end);
            const d = moment.duration(ms);
            const hourDiff = Math.abs(Math.floor(d.asHours()));

            if (hourDiff < 24) {
                reccurence.push({key: "day", label: this.$t("booking.reccurenceType.day")});
            }
            if (hourDiff < 168) {
                reccurence.push({key: "week", label: this.$t("booking.reccurenceType.week")});
            }
            if (Math.abs(start.diff(end, "month")) < 1) {
                reccurence.push({key: "month", label: this.$t("booking.reccurenceType.month")});
            }
            if (Math.abs(start.diff(end, "year")) < 1) {
                reccurence.push({key: "year", label: this.$t("booking.reccurenceType.year")});
            }
        }
        return [
            {key: "once", label: this.$t("booking.reccurenceType.once")},
            ...reccurence,
        ];
    }

    get canEditAdmin(): boolean {
      return this.isAdmin || this.canDoInEnvCompany("ADMIN_BOOKINGS");
    }

    get canEdit(): boolean {
      return this.canEditAdmin
        || this.isKeyManager
        || (this.currentUser && this.activeEvent?.createdBy === this.currentUser?.id);
    }

    get canGuestModify(): boolean {
        return !this.isAdmin
            && this.activeEvent?.createdBy !== this.currentUser.id
            && this.activeEvent?.user?.id === this.currentUser.id;
    }

    get isKeyManager(): boolean {
        return this.relatedKey?.legitimateKeyAccess?.keyManagementId;
    }

    get hasBookingEnded(): boolean {
        return new Date() > new Date(this.relatedKey?.currentBooking?.endDate || this.endDate);
    }

    get hasBookingStarted(): boolean {
        return new Date() > new Date(this.startDate);
    }

    get currentDate() {
        return new Date().toISOString();
    }

    get fieldRequired(): boolean {
        return !this.startDate || !this.endDate;
    }

    @Prop({default: false})
    public show: boolean;

    @Prop({default: null})
    public activeKey: any;

    @Prop({default: null})
    public user: any;

    @Prop({default: null})
    public activeEvent: any;

    @Prop({default: false})
    public editMode: boolean;

    private relatedKey: any = {};
    private reservation: any = {
        key: {
            id: null,
            name: null,
        },
        user: {
            id: null,
            name: null,
        },
    };
    private handToHandDialog: boolean = false;
    private isExceeding: boolean = false;
    private accessValue: string = "";
    private startDate: any = null;
    private endDate: string = "";
    private wording: any = null;
    private bookingReccurence: string = "once";
    private rules: object = formRules;
    private valid: boolean = true;
    private bookingName: string = "";
    private reccurenceLimit: string = "";
    private bookingCreation: boolean = false;
    private tryToSubmit: boolean = false;
    private conflicts: any[] = [];

    @Getter private currentUser: any;
    @Getter private isAdmin: boolean;
    @Getter private isMobile: boolean;
    @Getter private canDoInEnvCompany: (right: string) => boolean;

    @Watch("show")
    public handlerShow(newVal, oldVal) {
        if (newVal === true) {
            this.getKeyById();
            this.handToHandDialog = true;
            /*if (this.bookingStartingDate) {
                let date = moment(this.bookingStartingDate.date);
                date.set({hour: this.bookingStartingDate.hour, minute: 0, second: 0});
                if (date.format("YYYY-MM-DD HH:mm:ss") < this.currentDate) {
                    date = moment(this.currentDate);
                }
            } else */
            if (this.editMode && this.activeEvent) {
                this.startDate = moment(this.activeEvent.start).format();
                this.endDate = moment(this.activeEvent.end).format();
                this.bookingName = this.activeEvent.bookingName;
            } else if (!this.editMode && this.user.startDate && this.user.endDate) {
                this.startDate = moment(this.user.startDate).format();
                this.endDate = moment(this.user.endDate).format();
            } else {
                this.startDate = moment().format();
            }
        }
    }

    private reservationInfos(): any {
        this.reservation = {
            key: {
                id: this.relatedKey.id,
                name: this.relatedKey.name,
            },
            user: {
                id: this.user.id,
                name: this.user.identity,
            },
        };
    }

    private async accessExceeding(): Promise<void> {
      const payload = {
        userId: this.user.id,
        targetId: this.activeKey.id,
        targetType: "keys",
      };
      const userAccess: any[] = await this.$store.dispatch("keyAccesses/getByEntities", payload);

      const isUserKeyManager = userAccess.some((a) => !!a.keyManagementId);
      if (isUserKeyManager) {
        this.isExceeding = false;
        return;
      }

      this.isExceeding = userAccess.some((access) => {
          if ((access.startDate && access.endDate)
              && (this.startDate < access.startDate || this.endDate > access.endDate )) {
              return true;
          }
          if ((access.startDate && !access.endDate) && this.startDate < access.startDate) {
            return true;
          }
          if (access.endDate && this.bookingReccurence !== "once" && this.reccurenceLimit > access.endDate) {
            return true;
          }
          return false;
      });
    }

    private accessDate(): void {
        const accessValue = "";
        const userAccess = this.relatedKey?.keyAccess;
        if (!userAccess || userAccess.length === 0) {
            this.accessValue = accessValue;
            return;
        }

        userAccess.map((access) => {
            if (access && access.startDate && access.endDate) {
                this.accessValue += `${moment(access.startDate).format("YYYY-MM-DD HH:mm")} - ${moment(access.endDate).format("YYYY-MM-DD HH:mm")} `;
            }
        });

    }

    private mounted(): void {
        this.wording = {
            ok: this.$i18n.t("actions.next"),
            cancel: this.$i18n.t("actions.cancel"),
        };
    }

    private relayDisplayed(key): any {
        return key.Relays?.find((relay) => relay.type === "AGENCY")
            || key.Relays?.[0];
    }

    private closeDialog(): void {
        this.handToHandDialog = false;
        this.resetModal();
        this.$emit("closeModal");
    }

    private resetModal(): void {
        this.startDate = null;
        this.endDate = "";
        this.wording = {
            ok: this.$i18n.t("actions.next"),
            cancel: this.$i18n.t("actions.cancel"),
        };
        this.bookingReccurence = "once";
        this.valid = true;
        this.bookingName = "";
        this.tryToSubmit = false;
        this.conflicts = [];
    }

    private createBooking(): void {
        if (this.fieldRequired) {
            this.tryToSubmit = true;
            return;
        }
        this.tryToSubmit = false;
        this.bookingCreation = true;
        this.$store.dispatch("bookings/create", {
            booking: {
                name: this.bookingName,
                startDate: moment(this.startDate),
                endDate: moment(this.endDate),
                keyId: this.relatedKey.id,
                userId: this.user ? this.user.id : this.currentUser.id,
                reccurence: {
                    type: this.bookingReccurence,
                    limit: this.reccurenceLimit
                        ? moment(this.reccurenceLimit).set({hour: 23, minute: 59, second: 59})
                        : null,
                },
                notificationType: this.user.notificationType,
            },
        }).then((res) => {
            const noBookingCreated = res.noBookingCreated;
            if (noBookingCreated) {
                this.$store.commit("alerts/displayError", {
                    msg: `${this.$i18n?.t("alerts.planning.creationConflict")} `,
                });
            } else {
                this.$store.commit("alerts/displaySuccess", {
                    msg: `${this.$i18n?.t("alerts.planning.creationSuccess")} ${res.conflict ? "- " + this.$i18n?.t("alerts.planning.creationConflicts") : ""} `,
                });
            }
            this.bookingCreation = false;
            this.$emit("bookingCreated");
            this.closeDialog();
        }).catch((err) => {
            if (err?.response?.data?.noBookingCreated) {
                this.$store.commit("alerts/displayError", {
                    msg: `${this.$i18n?.t("booking.error.keyIsAlreadyBooked")} `,
                });
                this.bookingCreation = false;
                this.closeDialog();
                return;
            }
            this.$store.commit("alerts/displayError", {
                msg: err,
            });
            this.bookingCreation = false;
            this.closeDialog();
        });
    }

    private async editBooking() {
        await this.checkConflict();
        if (this.conflicts.length) {
            return;
        }
        if (this.fieldRequired) {
            this.tryToSubmit = true;
            return;
        }
        this.tryToSubmit = false;
        this.bookingCreation = true;
        this.$store.dispatch("bookings/update", {
            id: this.activeEvent.id,
            booking: {
                name: this.bookingName,
                startDate: new Date(this.startDate),
                endDate: new Date(this.endDate),
                reccurence: {
                    type: this.bookingReccurence,
                    limit: this.reccurenceLimit,
                },
            },
            alertCreator: this.canGuestModify,
        }).then(() => {
            this.$store.commit("alerts/displaySuccess", {
                msg: this.$i18n?.t("alerts.planning.editSuccess"),
            });
            this.bookingCreation = false;
            this.$emit("bookingCreated");
            this.closeDialog();
        }).catch((err) => {
            if (err?.response?.data?.noBookingCreated) {
                this.$store.commit("alerts/displayError", {
                    msg: `${this.$i18n?.t("booking.error.keyIsAlreadyBooked")} `,
                });
                this.bookingCreation = false;
                this.closeDialog();
                return;
            }
            this.$store.commit("alerts/displayError", {
                msg: err,
            });
            this.closeDialog();
        });
    }

    private deleteBooking(): void {
        this.$emit("deleteBooking", {booking: this.activeEvent});
        this.closeDialog();
    }

    private getKeyById() {
        if (this.editMode) {
            this.$store.dispatch("keys/getById", {
                id: this.activeKey.id,
                options: {
                    relays: true,
                    company: true,
                    holder: true,
                },
            }).then((key) => {
                this.relatedKey = { ...key, ...this.activeKey };
                this.reservationInfos();
                this.accessExceeding();
                this.accessDate();
            });
        } else {
            this.relatedKey = this.activeKey;
            this.reservationInfos();
            this.accessExceeding();
            this.accessDate();
        }
    }

    // Check if the key is not book yet
    private checkConflict(): Promise<void> {
        return this.$store.dispatch("bookings/conflict", {
            booking: {
                startDate: moment(this.startDate),
                endDate: moment(this.endDate),
                keyId: this.reservation.key.id,
                currentId: this.activeEvent.id,
            },
        }).then((conflicts) => {
            this.conflicts = conflicts.bookings.rows;
        });
    }
}
